/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import { KTIcon } from "../../../../../_metronic/helpers";
import { Loading } from "../../../../../ofindo/components/loading/Loading";
import supabase from "../../../../../ofindo/database/supabaseClient";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { useAuth } from "../../../../modules/auth";

// Hilfsfunktion zum Generieren der BildURLs
   const getImageUrl = (placesId, baseName: string, sizeSuffix: '' | '-x0.5', format: 'webp' | 'jpeg'): string => {
        const storageBaseUrl = `${process.env.REACT_APP_STORAGE_URL}/object/public/partner-profile/${placesId}`;
        return `${storageBaseUrl}/${baseName}${sizeSuffix}.${format}`;
    };


interface Place {
    id: string;
    name: string;
    address_line_1: string;
    address_line_2?: string;
    house_number: string;
    postal_code: string;
    city: string;
    country?: string;
    logo_url?: string;
    header_url?: string;
    design_color_1?: string;
    design_color_2?: string;
    role: string;
}

const PlacesList: React.FC = () => {
    const intl = useIntl();
    const { auth } = useAuth();
    const [places, setPlaces] = useState<Place[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchPlaces = async () => {
            if (!auth?.user?.id) {
                setError("No user session found");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                // 1. Hole die partner_profile_ids für den aktuellen User
                const { data: roleData, error: roleError } = await supabase
                    .from("partner_profile_roles")
                    .select("partner_profile_id, role")
                    .eq("user_id", auth.user.id);

                if (roleError) throw roleError;

                if (!roleData?.length) {
                    setPlaces([]);
                    setLoading(false);
                    return;
                }

                // 2. Hole die places_ids aus partner_profiles
                const partnerProfileIds = roleData.map((item) =>
                    item.partner_profile_id,
                );
                const { data: partnerProfiles, error: partnerProfileError } =
                    await supabase
                        .from("partner_profiles")
                        .select("id, places_id")
                        .in("id", partnerProfileIds);

                if (partnerProfileError) throw partnerProfileError;

                if (!partnerProfiles?.length) {
                    setPlaces([]);
                    setLoading(false);
                    return;
                }

                // 3. Hole die Places-Daten (ohne Öffnungszeiten)
                const placesIds = partnerProfiles
                    .map((profile) => profile.places_id)
                    .filter(Boolean);

                if (!placesIds.length) {
                    setPlaces([]);
                    setLoading(false);
                    return;
                }

                const { data: placesData, error: placesError } = await supabase
                    .from("places")
                    .select(`
                        id,
                        name,
                        address_line_1,
                        address_line_2,
                        house_number,
                        postal_code,
                        city,
                        country,
                        logo_url,
                        design_color_1,
                        design_color_2
                    `)
                    .in("id", placesIds);

                if (placesError) throw placesError;

                // Verbinde die Places-Daten mit den Rollen
                const placesWithRoles = placesData.map((place) => {
                    const roleEntry = roleData.find((role) => {
                        const profileWithPlace = partnerProfiles.find(
                            (profile) => profile.places_id === place.id,
                        );
                        return (
                            profileWithPlace &&
                            profileWithPlace.id === role.partner_profile_id
                        );
                    });

                    return {
                        ...place,
                        role: roleEntry
                            ? `PLACES.${roleEntry.role.toUpperCase()}`
                            : "PLACES.VIEWER",
                    };
                });

                setPlaces(placesWithRoles);
            } catch (error) {
                console.error("Error fetching places:", error);
                setError("Failed to fetch places");
            } finally {
                setLoading(false);
            }
        };

        fetchPlaces();
    }, [auth]);

    if (error) {
        return (
            <div className="card-body pt-2">
                <div className="text-center py-10">
                    <KTIcon
                        iconName="cross-circle"
                        className="fs-3x text-danger mb-5"
                    />
                    <div className="text-danger fw-semibold fs-6 mb-5">
                        {error}
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return <Loading />;
    }

    return (
        <div className="card-body pt-2">
            {places.length > 0 ? (
                places.map((place) => (
                    <div
                        key={place.id}
                        className="d-flex align-items-sm-center mb-3"
                    >
                        {/* begin::Symbol */}
                        <Link to={`/places/details/${place.id}`}>
                            <div
                                className='image-input image-input-outline me-4'
                                style={{
                                    width: '110px',
                                    height: '110px',
                                }}
                            >
                                <div
                                    className='image-input-wrapper w-100 h-100 border border-4 border-white rounded shadow-sm cursor-pointer d-flex align-items-center justify-content-center'
                                    style={{
                                        backgroundColor: !place.logo_url ? (place.design_color_1 || '#f5f8fa') : 'transparent',
                                    }}
                                >
                                    {place.logo_url ? (
                                        <picture className='d-block w-100 h-100 rounded-2 overflow-hidden'>
                                <source
                                    srcSet={`${getImageUrl(place.id, place.logo_url, '-x0.5', 'webp')} 1x, ${getImageUrl(place.id, place.logo_url, '', 'webp')} 2x`}
                                    type="image/webp"
                                />
                                <source
                                    srcSet={`${getImageUrl(place.id, place.logo_url, '-x0.5', 'jpeg')} 1x, ${getImageUrl(place.id, place.logo_url, '', 'jpeg')} 2x`}
                                    type="image/jpeg"
                                />
                                <img
                                    src={getImageUrl(place.id, place.logo_url, '', 'jpeg')}
                                    alt={intl.formatMessage({ id: 'PLACES.LOGO_IMAGE_ALT' })}
                                    className='w-100 h-100'
                                    style={{
                                        objectFit: 'contain',
                                        objectPosition: 'center',
                                        display: 'block',
                                    }}
                                />
                            </picture>
                                    ) : (
                                        <span className="symbol-label fs-1 fw-bold">
                                            {place.name.charAt(0).toUpperCase()}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Link>
                        {/* end::Symbol */}

                        {/* begin::Content */}
                        <div className="d-flex flex-row-fluid align-items-center flex-wrap my-lg-0 me-2">
                            {/* begin::Title */}
                            <div className="flex-grow-1 my-lg-0 my-2 me-2">
                                <Link
                                    to={`/places/details/${place.id}`}
                                    className="fw-bold text-hover-primary fs-5 align-items-center"
                                >
                                    <span>{place.name}</span>
                                </Link>
                                <span className="text-muted d-block fs-5 pt-1">
                                    {place.address_line_1} {place.house_number}
                                </span>
                            </div>
                            {/* end::Title */}

                            {/* begin::Section */}
                            <div className="d-flex align-items-center">
                                <span className="badge badge-light fs-8 ms-2">
                                    {intl.formatMessage({ id: place.role })}
                                </span>
                                <a
                                    href="#"
                                    className="btn btn-icon btn-light btn-sm border-0 visually-hidden"
                                >
                                    <KTIcon
                                        iconName="arrow-right"
                                        className="fs-2 text-primary"
                                    />
                                </a>
                            </div>
                            {/* end::Section */}
                        </div>
                        {/* end::Content */}
                    </div>
                ))
            ) : (
                <div className="text-center py-10">
                    <KTIcon
                        iconName="abstract-24"
                        className="fs-3x text-gray-400 mb-5"
                    />
                    <div className="text-gray-600 fw-semibold fs-6 mb-5">
                        {intl.formatMessage({ id: "PLACES.NO_PLACES" })}
                    </div>
                    <Link to="/places/new" className="btn btn-primary">
                        {intl.formatMessage({ id: "PLACES.ADD_NEW" })}
                    </Link>
                </div>
            )}
        </div>
    );
};

export default PlacesList;
